// Week-average card for the Log view — kcal/macro averages, adherence %, and
// logged-days/steps/sleep line, over the currently visible week. Reuses
// avgOf (same roll-up as Table's weekly-average row and Calendar's Week avg
// cell), tinted by adhColor(avg.adh) via a state modifier class
// (np-log-week-avg--good/--warn/--bad/--none) instead of inline style — same
// CSS-custom-property pattern as .np-plan-bar's status modifiers elsewhere in
// nutrition.css. That's what lets the "none" state get a solid white bg in
// light theme (--white-05 alone reads as invisible there, like every other
// --white-05 card in this file) — an inline style would always win over a
// [data-theme="light"] override, a plain class doesn't have that problem.

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { adhColor } from "../../../../../utils/nutrition-progress";
import { fmtSleep, translateCalendarGrid, translateLog } from "./viewTokens";

export const LogWeekAverage = ({ avg }) => {
  const { t } = useTranslation("athlete-portal");

  if (!avg || avg.n === 0) {
    return (
      <div className="np-log-week-avg np-log-week-avg--none">
        <div className="np-log-week-avg__title">{translateLog(t, "weekAverageTitle")}</div>
        <div className="np-log-week-avg__empty">{translateLog(t, "noLoggedDays")}</div>
      </div>
    );
  }

  const token = adhColor(avg.adh);

  // Macro-letter labels are reused from calendarGrid (not a new key) — the
  // Spanish translation uses "G" (grasa) for fat, not "F", so this also
  // fixes what would otherwise be a wrong letter under es locale.
  const macroLabel = (key) => translateCalendarGrid(t, key);

  const metaParts = [translateLog(t, "daysLogged", { n: avg.n, total: avg.total })];
  if (avg.steps != null) {
    metaParts.push(translateLog(t, "stepsValue", { steps: avg.steps.toLocaleString() }));
  }
  if (avg.sleep != null) {
    metaParts.push(translateLog(t, "sleepValue", { sleep: fmtSleep(avg.sleep) }));
  }

  return (
    <div className={`np-log-week-avg np-log-week-avg--${token}`}>
      <div className="np-log-week-avg__title">{translateLog(t, "weekAverageTitle")}</div>
      {avg.consumed && (
        <div className="np-log-week-avg__kcal">
          {translateLog(t, "kcalValue", { kcal: avg.consumed.kcal.toLocaleString() })}
        </div>
      )}
      {avg.consumed && (
        <div className="np-log-week-avg__macros">
          {macroLabel("macroProteinShort")} {avg.consumed.proteins} · {macroLabel("macroCarbsShort")}{" "}
          {avg.consumed.carbs} · {macroLabel("macroFatShort")} {avg.consumed.fats}
        </div>
      )}
      {avg.adh != null && (
        <div className="np-log-week-avg__adherence">
          {translateLog(t, "adherencePct", { pct: avg.adh })}
        </div>
      )}
      <div className="np-log-week-avg__meta">{metaParts.join(" · ")}</div>
    </div>
  );
};

LogWeekAverage.propTypes = {
  avg: PropTypes.shape({
    n: PropTypes.number,
    total: PropTypes.number,
    consumed: PropTypes.object,
    adh: PropTypes.number,
    steps: PropTypes.number,
    sleep: PropTypes.number,
  }),
};

export default LogWeekAverage;
